export interface MeasurementTimeContext {
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
}

const validTimezone = (timezone?: string | null): string => {
    const candidate = timezone || 'UTC';
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: candidate }).format(new Date());
        return candidate;
    } catch {
        return 'UTC';
    }
};

export const measurementTimezone = (measurement?: MeasurementTimeContext | null): string => (
    validTimezone(measurement?.measuredTimezone)
);

export const formatMeasurementTimestamp = (
    value: string | Date | null | undefined,
    measurement?: MeasurementTimeContext | null,
    options: { seconds?: boolean; dateOnly?: boolean; includeZone?: boolean } = {},
): string => {
    if (!value) return '—';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const timezone = measurementTimezone(measurement);
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...(options.dateOnly ? {} : {
            hour: '2-digit',
            minute: '2-digit',
            ...(options.seconds === false ? {} : { second: '2-digit' }),
            hour12: false,
        }),
        ...(options.includeZone === false ? {} : { timeZoneName: 'short' }),
    });
    return formatter.format(date);
};

export const formatUtcTimestamp = (value: string | Date | null | undefined): string => {
    if (!value) return '—';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return `${new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(date)} UTC`;
};

export const measurementTimeCsvFields = (value: string | null | undefined, measurement?: MeasurementTimeContext | null) => ({
    measuredAtUtc: value ? new Date(value).toISOString() : '',
    measuredAtLocal: value ? formatMeasurementTimestamp(value, measurement, { includeZone: false }) : '',
    measuredTimezone: measurementTimezone(measurement),
});

export const measurementDateKey = (value: string | null | undefined, measurement?: MeasurementTimeContext | null): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: measurementTimezone(measurement), year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
        if (part.type !== 'literal') result[part.type] = part.value;
        return result;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
};

const zonedLocalToUtc = (year: number, month: number, day: number, timeZone: string): Date => {
    const desired = Date.UTC(year, month - 1, day, 0, 0, 0);
    let guess = desired;
    for (let attempt = 0; attempt < 3; attempt += 1) {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
        }).formatToParts(new Date(guess)).reduce<Record<string, string>>((result, part) => {
            if (part.type !== 'literal') result[part.type] = part.value;
            return result;
        }, {});
        const represented = Date.UTC(
            Number(parts.year), Number(parts.month) - 1, Number(parts.day),
            Number(parts.hour), Number(parts.minute), Number(parts.second),
        );
        guess += desired - represented;
    }
    return new Date(guess);
};

export const measurementLocalDayUtcRange = (
    value: string | null | undefined,
    measurement?: MeasurementTimeContext | null,
): [Date, Date] | null => {
    const key = measurementDateKey(value, measurement);
    if (!key) return null;
    const [year, month, day] = key.split('-').map(Number);
    const timeZone = measurementTimezone(measurement);
    const start = zonedLocalToUtc(year, month, day, timeZone);
    const nextLocalDate = new Date(Date.UTC(year, month - 1, day + 1));
    const endExclusive = zonedLocalToUtc(
        nextLocalDate.getUTCFullYear(), nextLocalDate.getUTCMonth() + 1, nextLocalDate.getUTCDate(), timeZone,
    );
    return [start, new Date(endExclusive.getTime() - 1)];
};
